<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import Announcements from './Announcements.vue'
import ProductCard from './ProductCard.vue'
import Receipt from './Receipt.vue'
import { capitalize, clock, fromNow, krShort, shortDay, shortTime, weekday } from '../lib/format'
import logoUrl from '../images/logo.webp'
import wordmarkUrl from '../images/wordmark.webp'

const props = defineProps({ initial: { type: Object, required: true } })

const bakeDays = ref(props.initial.bakeDays)
const announcements = ref(props.initial.announcements ?? [])
const selectedId = ref(bakeDays.value[0]?.id ?? null)
const cart = reactive({}) // { productId: quantity } for the selected bake day
const now = ref(Date.now())
const orderPlaced = ref(false)
const receiptEl = ref(null)
const receiptInView = ref(false) // mobile: hide the shortcut bar once the receipt itself is on screen
const scrolled = ref(false)

const bakeDay = computed(() => bakeDays.value.find((day) => day.id === selectedId.value) ?? null)
const products = computed(() => bakeDay.value?.products ?? [])

const lines = computed(() =>
  products.value
    .filter((product) => cart[product.id] > 0)
    .map((product) => ({ product, quantity: cart[product.id], totalOre: cart[product.id] * product.priceOre })),
)
const itemCount = computed(() => lines.value.reduce((sum, line) => sum + line.quantity, 0))
const totalOre = computed(() => lines.value.reduce((sum, line) => sum + line.totalOre, 0))

function setQuantity(product, quantity) {
  cart[product.id] = Math.max(0, Math.min(quantity, product.remaining))
}

function clearCart() {
  Object.keys(cart).forEach((id) => delete cart[id])
}

// Keep the cart honest when fresh stock numbers arrive from the server.
function applyStorefront(data) {
  bakeDays.value = data.bakeDays
  if (data.announcements) announcements.value = data.announcements
  if (!bakeDays.value.some((day) => day.id === selectedId.value)) {
    selectedId.value = bakeDays.value[0]?.id ?? null
    clearCart()
  }
  products.value.forEach((product) => {
    if (cart[product.id] > product.remaining) cart[product.id] = product.remaining
  })
}

async function refresh() {
  try {
    const response = await fetch('/api/storefront', { headers: { Accept: 'application/json' } })
    if (response.ok) applyStorefront(await response.json())
  } catch {
    // Offline or server hiccup – the numbers we have are good enough.
  }
}

function onPlaced() {
  orderPlaced.value = true
  clearCart()
  refresh()
}

function onReset() {
  orderPlaced.value = false
}

function scrollToReceipt() {
  receiptEl.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

watch(selectedId, clearCart)

let ticker
let poller
let receiptObserver
const onVisible = () => document.visibilityState === 'visible' && refresh()
const onScroll = () => (scrolled.value = window.scrollY > 8)
onMounted(() => {
  ticker = setInterval(() => (now.value = Date.now()), 30_000)
  // Keep stock and news fresh while the page is open; new posts pop in as notifications.
  poller = setInterval(() => document.visibilityState === 'visible' && refresh(), 120_000)
  document.addEventListener('visibilitychange', onVisible)
  window.addEventListener('scroll', onScroll, { passive: true })
  receiptObserver = new IntersectionObserver(([entry]) => (receiptInView.value = entry.isIntersecting), {
    rootMargin: '0px 0px -80px 0px',
  })
  watch(receiptEl, (el) => el && receiptObserver.observe(el), { immediate: true })
  // Next frame: reading scrollY during mount would force an extra layout before the first paint.
  requestAnimationFrame(onScroll)
})
onBeforeUnmount(() => {
  clearInterval(ticker)
  clearInterval(poller)
  document.removeEventListener('visibilitychange', onVisible)
  window.removeEventListener('scroll', onScroll)
  receiptObserver?.disconnect()
})

const closesIn = computed(() => bakeDay.value && fromNow(bakeDay.value.ordersCloseAt, now.value))
const soldOutEverything = computed(() => products.value.length > 0 && products.value.every((p) => p.remaining <= 0))
</script>

<template>
  <header class="topbar" :class="{ 'is-scrolled': scrolled }">
    <div class="topbar__inner">
      <a class="topbar__brand" href="/" aria-label="Den Salte Klejne – forsiden">
        <img :src="wordmarkUrl" alt="Den Salte Klejne" width="617" height="132" />
      </a>
      <nav v-if="bakeDays.length > 1" class="day-picker" aria-label="Vælg bagedag">
        <button
          v-for="day in bakeDays"
          :key="day.id"
          type="button"
          class="day-picker__day"
          :aria-pressed="day.id === selectedId"
          @click="selectedId = day.id"
        >
          {{ shortDay(day.pickupStartsAt) }}
        </button>
      </nav>
      <Announcements :items="announcements" />
    </div>
  </header>

  <div class="shop">
    <template v-if="bakeDay">
      <section class="hero">
        <nav v-if="bakeDays.length > 1" class="day-picker day-picker--hero" aria-label="Vælg bagedag">
          <button
            v-for="day in bakeDays"
            :key="day.id"
            type="button"
            class="day-picker__day"
            :aria-pressed="day.id === selectedId"
            @click="selectedId = day.id"
          >
            {{ shortDay(day.pickupStartsAt) }}
          </button>
        </nav>
        <div class="hero__grid">
          <div class="hero__intro">
            <h1 class="hero__title">forudbestil til {{ weekday(bakeDay.pickupStartsAt) }}</h1>
            <p class="hero__lede">
              Vælg, hvad du gerne vil have, så lægger vi det til side. Der bages kun et bestemt antal af hver slags,
              og du betaler ved afhentning.
            </p>
          </div>
          <div class="hero__side">
            <dl class="facts">
              <div>
                <dt>Bestil senest</dt>
                <dd>{{ capitalize(weekday(bakeDay.ordersCloseAt)) }} {{ clock(bakeDay.ordersCloseAt) }}</dd>
                <dd class="facts__aside">Lukker {{ closesIn }}</dd>
              </div>
              <div>
                <dt>Afhentning</dt>
                <dd>{{ shortDay(bakeDay.pickupStartsAt) }}</dd>
                <dd class="facts__aside">kl. {{ shortTime(bakeDay.pickupStartsAt) }}–{{ shortTime(bakeDay.pickupEndsAt) }}</dd>
              </div>
              <div>
                <dt>Betaling</dt>
                <dd>Ved afhentning</dd>
              </div>
            </dl>
          </div>
        </div>
        <p v-if="bakeDay.note" class="hero__note">{{ bakeDay.note }}</p>
      </section>

      <div class="shop__body">
        <section class="window" aria-label="Dagens bagværk">
          <p v-if="soldOutEverything" class="window__soldout">
            Alt er lagt til side til denne bagedag. Tak! Kig forbi igen til næste gang.
          </p>
          <div class="window__grid">
            <ProductCard
              v-for="(product, index) in products"
              :key="product.id"
              :product="product"
              :priority="index < 2"
              :in-cart="cart[product.id] || 0"
              @update="(quantity) => setQuantity(product, quantity)"
            />
          </div>
        </section>

        <aside ref="receiptEl" class="shop__receipt" aria-label="Din kvittering">
          <Receipt
            :bake-day="bakeDay"
            :lines="lines"
            :total-ore="totalOre"
            @placed="onPlaced"
            @reset="onReset"
            @stale="applyStorefront"
          />
        </aside>
      </div>

      <Transition name="bar">
        <button
          v-if="(itemCount > 0 || orderPlaced) && !receiptInView"
          type="button"
          class="cart-bar"
          @click="scrollToReceipt"
        >
          <span v-if="orderPlaced && itemCount === 0">Din bestilling er modtaget</span>
          <span v-else>{{ itemCount }} {{ itemCount === 1 ? 'vare' : 'varer' }} for {{ krShort(totalOre) }}</span>
          <span class="cart-bar__cta">{{ orderPlaced && itemCount === 0 ? 'Se kvittering' : 'Gå til bestilling' }}</span>
        </button>
      </Transition>
    </template>

    <section v-else class="hero resting">
      <h1 class="hero__title">ovnen hviler</h1>
      <p class="hero__lede">Der er ingen åbne bagedage lige nu. Næste gang bliver lagt op her, så kig forbi igen snart.</p>
    </section>

    <footer class="footer">
      <img class="footer__logo" :src="logoUrl" alt="Den Salte Klejne" width="400" height="612" loading="lazy" decoding="async" />
      <div class="footer__meta">
        <a class="social" href="https://www.instagram.com/densalteklejne/" target="_blank" rel="noopener">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="5" />
            <circle cx="12" cy="12" r="4" />
            <circle cx="17.2" cy="6.8" r="1.1" fill="currentColor" stroke="none" />
          </svg>
          @densalteklejne
        </a>
        <p>Hjemmebageri, hvor alt bages i små portioner</p>
        <p>Forudbestil, og betal når du henter</p>
      </div>
    </footer>
  </div>
</template>
